import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  TrendingUp, DollarSign, Calendar, BarChart3, Loader2, Users,
  Target, Receipt, Gauge, ArrowUpRight, ArrowDownRight, Percent, UserSearch, FileText
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell } from "recharts";
import PageHeader from "../components/PageHeader";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-slate-400 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-bold" style={{ color: p.color || "#f7c935" }}>{p.value?.toLocaleString("fr-FR")} {p.name === "ca" ? "EUR" : ""}</p>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color, trend }) => (
  <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-0.5 text-[10px] font-bold ${trend >= 0 ? "text-green-400" : "text-red-400"}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="text-[10px] text-slate-500 uppercase">{label}</div>
    <div className="text-xl font-black mt-0.5" style={{ color }}>{value}</div>
    {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const PIE_COLORS = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#f97316", "#64748b"];

export default function FinancialPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [pilotage, setPilotage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("commercial");

  useEffect(() => {
    Promise.all([
      api.get("/admin/financial").catch(() => ({ data: null })),
      api.get("/admin/pilotage").catch(() => ({ data: null })),
    ]).then(([fin, pil]) => {
      setData(fin.data);
      setPilotage(pil.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Chargement...</div>;

  const com = pilotage?.commercial || {};
  const fin = pilotage?.financier || {};
  const finData = data || {};

  const sourcePieData = Object.entries(com.source_stats || {}).map(([name, value]) => ({ name, value }));
  const modulePieData = Object.entries(com.module_stats || {}).map(([name, value]) => ({ name: name.substring(0, 20), value }));

  const tabs = [
    { key: "commercial", label: "Commercial", icon: Target },
    { key: "financier", label: "Financier", icon: DollarSign },
    { key: "ca", label: "CA & Previsionnel", icon: TrendingUp },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="financial-page">
      <PageHeader
        icon={<Gauge />}
        title="Pilotage"
        description="Indicateurs de performance commerciale et financière"
        testId="financial-header"
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-3 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`pilotage-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${tab === t.key ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Commercial tab */}
      {tab === "commercial" && (
        <div className="space-y-5" data-testid="pilotage-commercial">
          {/* KPIs row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={UserSearch} label="Total Prospects" value={com.total_prospects || 0} color="#3b82f6"
              sub={`${com.prospects_nouveau || 0} nouveaux`} />
            <KpiCard icon={Percent} label="Taux conversion Prospect" value={`${com.taux_conversion_prospect || 0}%`} color="#22c55e"
              sub={`${com.prospects_converti || 0} convertis`} />
            <KpiCard icon={Target} label="Pipeline CRM" value={`${(com.pipeline_pondere || 0).toLocaleString("fr-FR")} EUR`} color="#8b5cf6"
              sub={`${com.opps_active || 0} opportunites actives`} />
            <KpiCard icon={Percent} label="Taux conversion CRM" value={`${com.taux_conversion_crm || 0}%`} color="#f7c935"
              sub={`${com.opps_gagne || 0} gagnes sur ${com.total_opps || 0}`} />
          </div>

          {/* Funnel & Pie charts */}
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Funnel */}
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">Entonnoir de conversion</h3>
              <div className="space-y-2">
                {[
                  { label: "Prospects", value: com.total_prospects || 0, color: "#3b82f6", pct: 100 },
                  { label: "Contactes", value: com.prospects_contacte || 0, color: "#8b5cf6", pct: Math.round((com.prospects_contacte || 0) / Math.max(com.total_prospects, 1) * 100) },
                  { label: "Qualifies", value: com.prospects_qualifie || 0, color: "#f59e0b", pct: Math.round((com.prospects_qualifie || 0) / Math.max(com.total_prospects, 1) * 100) },
                  { label: "Convertis CRM", value: com.prospects_converti || 0, color: "#22c55e", pct: Math.round((com.prospects_converti || 0) / Math.max(com.total_prospects, 1) * 100) },
                  { label: "Gagnes", value: com.opps_gagne || 0, color: "#f7c935", pct: Math.round((com.opps_gagne || 0) / Math.max(com.total_prospects, 1) * 100) },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="text-[10px] text-slate-500 w-24 text-right">{step.label}</div>
                    <div className="flex-1 h-7 rounded bg-white/[0.03] relative overflow-hidden">
                      <div className="h-full rounded flex items-center px-2 transition-all" style={{ width: `${Math.max(step.pct, 4)}%`, background: `${step.color}25` }}>
                        <span className="text-[10px] font-bold" style={{ color: step.color }}>{step.value}</span>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 w-10 text-right">{step.pct}%</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Source distribution */}
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">Repartition par source</h3>
              {sourcePieData.length === 0 ? (
                <p className="text-xs text-slate-500 py-8 text-center">Aucune donnee</p>
              ) : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="50%" height={180}>
                    <PieChart>
                      <Pie data={sourcePieData} dataKey="value" cx="50%" cy="50%" outerRadius={70} strokeWidth={0}>
                        {sourcePieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5">
                    {sourcePieData.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-slate-400 flex-1 truncate">{s.name}</span>
                        <span className="font-bold">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Module interest chart */}
          {modulePieData.length > 0 && (
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">Modules les plus demandes</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={modulePieData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                  <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} />
                  <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={9} tickLine={false} width={130} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Financier tab */}
      {tab === "financier" && (
        <div className="space-y-5" data-testid="pilotage-financier">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={Receipt} label="CA Facture" value={`${(fin.ca_facture || 0).toLocaleString("fr-FR")} EUR`} color="#f7c935" />
            <KpiCard icon={DollarSign} label="CA Encaisse" value={`${(fin.ca_encaisse || 0).toLocaleString("fr-FR")} EUR`} color="#22c55e"
              sub={`Taux: ${fin.taux_encaissement || 0}%`} />
            <KpiCard icon={FileText} label="Factures" value={fin.total_factures || 0} color="#3b82f6"
              sub={`${fin.factures_payees || 0} payees, ${fin.factures_impayees || 0} impayees`} />
            <KpiCard icon={BarChart3} label="Panier moyen" value={`${(fin.panier_moyen || 0).toLocaleString("fr-FR")} EUR`} color="#8b5cf6" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">Encaissement</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Taux d'encaissement</span>
                  <span className="text-sm font-bold text-green-400">{fin.taux_encaissement || 0}%</span>
                </div>
                <div className="w-full h-3 rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-green-400 transition-all" style={{ width: `${fin.taux_encaissement || 0}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500">Factures payees</div>
                    <div className="text-lg font-bold text-green-400">{fin.factures_payees || 0}</div>
                  </div>
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500">Factures impayees</div>
                    <div className="text-lg font-bold text-red-400">{fin.factures_impayees || 0}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">Transformation devis</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Taux de transformation</span>
                  <span className="text-sm font-bold text-[#f7c935]">{fin.taux_transformation_devis || 0}%</span>
                </div>
                <div className="w-full h-3 rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#f7c935] to-[#f59e0b] transition-all" style={{ width: `${fin.taux_transformation_devis || 0}%` }} />
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500">Total devis</div>
                    <div className="text-lg font-bold text-slate-300">{fin.total_devis || 0}</div>
                  </div>
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500">Devis acceptes</div>
                    <div className="text-lg font-bold text-[#f7c935]">{fin.devis_acceptes || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CA & Previsionnel tab */}
      {tab === "ca" && finData && (
        <div className="space-y-5" data-testid="pilotage-ca">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={TrendingUp} label="CA Annuel" value={`${(finData.summary?.total_annual_ca || 0).toLocaleString("fr-FR")} EUR`} color="#f7c935" />
            <KpiCard icon={DollarSign} label="Revenu mensuel actif" value={`${(finData.summary?.active_monthly_revenue || 0).toLocaleString("fr-FR")} EUR`} color="#10b981" />
            <KpiCard icon={BarChart3} label="Accompagnements actifs" value={finData.summary?.active_accompaniments || 0} color="#3b82f6" />
            <KpiCard icon={Calendar} label="Termines" value={finData.summary?.terminated_accompaniments || 0} color="#8b5cf6" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">CA Mensuel</h3>
              {(finData.monthly || []).length === 0 ? (
                <p className="text-xs text-slate-500 py-8 text-center">Aucune donnee</p>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={finData.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="ca" fill="#f7c935" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
              <h3 className="text-sm font-bold mb-4">Previsionnel</h3>
              {(finData.forecast || []).length === 0 ? (
                <p className="text-xs text-slate-500 py-8 text-center">Aucun previsionnel</p>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={finData.forecast}>
                    <defs>
                      <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f7c935" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f7c935" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={10} tickLine={false} />
                    <YAxis stroke="#64748b" fontSize={10} tickLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="ca" stroke="#f7c935" fill="url(#colorForecast)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
